import React, { useState } from "react";

export default function BankStatementStep({ setCurrentStep, selectedOffer }) {
  // States: 'selection', 'redirecting', 'refresh', 'fetching', 'success'
  const [flowState, setFlowState] = useState("selection");

  // Dynamically fetch lender name from your offer object, fallback to "DigitMoney"
  const lenderName = selectedOffer?.lender_name || "DigitMoney";

  // Handle the initial click on "Account Aggregator"
  const handleAggregatorClick = () => {
    setFlowState("redirecting");
    
    // Simulate the user being redirected and then returning after 2.5 seconds
    setTimeout(() => {
      setFlowState("refresh");
    }, 2500);
  };

  // Handle the user clicking the "Refresh Document" button
  const handleRefreshClick = () => {
    setFlowState("fetching");
    
    // Simulate API fetch delay, then show success
    setTimeout(() => {
      setFlowState("success");
    }, 2000);
  };

  const handleSubmit = () => {
    console.log("Bank Statement verified successfully");
    // Move to next step in your flow
    setCurrentStep(13); // Adjust this step number as needed
  };

  return (
    <div className="w-full max-w-[400px] mx-auto bg-white min-h-[100dvh] font-sans flex flex-col relative pb-[100px]">
      
      {/* Header & Progress Bar (No hardcoded image, purely dynamic text) */}
      <div className="px-5 pt-6 pb-2 bg-white z-10 sticky top-0">
        <div className="flex justify-center mb-5">
          <div className="text-center leading-none">
            <h1 className="text-[#205A6D] font-bold text-[24px] tracking-tight">
              {lenderName}
            </h1>
            <p className="text-[#7ea4b1] text-[9px] font-bold tracking-widest mt-1.5 uppercase">
              Loans made Easy
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <button onClick={() => setCurrentStep(10)} className="w-5 h-5 flex items-center justify-start text-[#4C5564] hover:opacity-70 transition-opacity">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          
          <div className="flex-1 mx-4 h-[4px] bg-[#CCF8E1] rounded-full overflow-hidden">
            <div className="h-full bg-[#12F280] w-[51%] rounded-full transition-all duration-500"></div>
          </div>
          <span className="text-[10px] text-[#4C5564] font-semibold leading-[18px]">51%</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-5 pt-4 flex-1">
        <h2 className="text-[20px] font-bold text-[#1b1b1b] leading-[26px] tracking-tight mb-2 pr-4">
          Share your Bank Statement of Salary Account
        </h2>
        <p className="text-[13px] text-[#979797] font-normal leading-[18px] mb-8 pr-4">
          We check salary credits, banking behaviour and debt burden ratio.
        </p>

        {/* STATE 1: SELECTION */}
        {flowState === "selection" && (
          <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            
            {/* Option 1: Account Aggregator (Recommended) */}
            <div className="relative">
              <div className="absolute -top-2.5 right-6 bg-[#FFF2E5] border border-[#FFD6B3] text-[#FF7A00] text-[9px] font-bold px-2 py-0.5 rounded-[6px] uppercase tracking-wider z-10">
                Recommended
              </div>
              <button 
                onClick={handleAggregatorClick}
                className="w-full flex items-center justify-between p-4 border border-[#92D8B4] rounded-[16px] bg-[rgba(146,216,180,0.05)] text-left transition-all active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-[10px] bg-[#E8F6EF] flex items-center justify-center flex-none border border-[#D1EFE2]">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#35614C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                  </div>
                  <div>
                    <h3 className="text-[14px] font-bold text-[#1b1b1b] leading-tight mb-1">Account Aggregator</h3>
                    <p className="text-[12px] text-[#979797] font-normal leading-tight">Instant, secure, RBI-approved.</p>
                  </div>
                </div>
                <div className="text-[#979797]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </div>
              </button>
            </div>

            {/* Option 2: Net Banking */}
            <button className="w-full flex items-center justify-between p-4 border border-[#E5E5E5] rounded-[16px] bg-white text-left transition-all active:scale-[0.98] hover:border-[#92D8B4]">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-[10px] bg-[#F5F5F5] flex items-center justify-center flex-none border border-[#E5E5E5]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b6b6b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="22" width="16" height="2"></rect><path d="M4 22V6"></path><path d="M20 22V6"></path><path d="M12 22V6"></path><path d="M2 6l10-4 10 4"></path></svg>
                </div>
                <div>
                  <h3 className="text-[14px] font-bold text-[#1b1b1b] leading-tight mb-1">Net Banking</h3>
                  <p className="text-[12px] text-[#979797] font-normal leading-tight">Fetch statement via secure login.</p>
                </div>
              </div>
              <div className="text-[#979797]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </div>
            </button>

            {/* Option 3: Upload PDF */}
            <button className="w-full flex items-center justify-between p-4 border border-[#E5E5E5] rounded-[16px] bg-white text-left transition-all active:scale-[0.98] hover:border-[#92D8B4]">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-[10px] bg-[#F5F5F5] flex items-center justify-center flex-none border border-[#E5E5E5]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b6b6b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                </div>
                <div>
                  <h3 className="text-[14px] font-bold text-[#1b1b1b] leading-tight mb-1">Upload PDF</h3>
                  <p className="text-[12px] text-[#979797] font-normal leading-tight">Last 3-6 months of your salary account.</p>
                </div>
              </div>
              <div className="text-[#979797]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </div>
            </button>

          </div>
        )}

        {/* STATE 2: REDIRECTING */}
        {flowState === "redirecting" && (
          <div className="border border-dashed border-[#92D8B4] rounded-[20px] bg-[rgba(146,216,180,0.03)] p-8 flex flex-col items-center justify-center text-center animate-in fade-in duration-500 h-[220px]">
            <div className="w-[50px] h-[50px] border-[3px] border-[#E8F6EF] border-t-[#0AAB62] rounded-full animate-spin mb-6"></div>
            <h3 className="text-[14px] font-bold text-[#1b1b1b] mb-2">Redirecting to Account Aggregator...</h3>
            <p className="text-[12px] text-[#979797] font-normal px-4 leading-relaxed">
              Complete the consent on the AA app<br/>and return here.
            </p>
          </div>
        )}

        {/* STATE 3: REFRESH (Back from AA) */}
        {flowState === "refresh" && (
          <div className="border border-[#92D8B4] rounded-[20px] bg-[rgba(146,216,180,0.05)] p-5 flex flex-col justify-center animate-in fade-in duration-500 h-[220px]">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#E8F6EF] flex items-center justify-center flex-none border border-[#D1EFE2]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#35614C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path></svg>
              </div>
              <div>
                <h3 className="text-[14px] font-bold text-[#1b1b1b] leading-tight mb-1">Back from Account Aggregator</h3>
                <p className="text-[12px] text-[#979797] font-normal leading-tight">Tap refresh to pull your documents.</p>
              </div>
            </div>
            
            <button 
              onClick={handleRefreshClick}
              className="w-full bg-[#D1EFE2] text-[#0AAB62] font-bold text-[15px] rounded-[14px] py-4 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              Refresh Document 
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path></svg>
            </button>
          </div>
        )}

        {/* STATE 4: FETCHING */}
        {flowState === "fetching" && (
          <div className="border border-dashed border-[#92D8B4] rounded-[20px] bg-[rgba(146,216,180,0.03)] p-8 flex flex-col items-center justify-center text-center animate-in fade-in duration-500 h-[220px]">
            <div className="w-[50px] h-[50px] border-[3px] border-[#E8F6EF] border-t-[#0AAB62] rounded-full animate-spin mb-6"></div>
            <h3 className="text-[14px] font-bold text-[#1b1b1b]">Fetching your statement...</h3>
          </div>
        )}

        {/* STATE 5: SUCCESS */}
        {flowState === "success" && (
          <div className="animate-in fade-in zoom-in-95 duration-500">
            <div className="border border-dashed border-[#92D8B4] rounded-[20px] bg-[rgba(146,216,180,0.03)] p-8 flex flex-col items-center justify-center text-center h-[220px]">
              <div className="w-[56px] h-[56px] bg-[#12F280] rounded-full flex items-center justify-center mb-5 shadow-sm shadow-[#12F280]/20">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
              <h3 className="text-[15px] font-bold text-[#1b1b1b] mb-1.5">Account Aggregator Added</h3>
              <p className="text-[12px] text-[#979797] font-normal">
                Statement pulled successfully.
              </p>
            </div>
            
            <div className="flex justify-center mt-6">
              <button 
                onClick={() => setFlowState("selection")}
                className="text-[13px] text-[#979797] font-medium underline underline-offset-4 hover:text-black transition-colors"
              >
                Use a different method
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Sticky Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full max-w-[400px] bg-gradient-to-t from-white via-white to-transparent px-4 pt-10 pb-6">
        <button 
          onClick={handleSubmit}
          disabled={flowState !== "success"}
          className={`w-full font-bold text-[16px] leading-[19px] rounded-[15px] py-4 flex items-center justify-center transition-all ${
            flowState === "success" 
            ? "bg-[#000000] text-[#FFFFFF] active:scale-[0.98] shadow-lg shadow-black/10" 
            : "bg-[#000000] text-[#FFFFFF] opacity-90 cursor-not-allowed"
          }`}
        >
          Share Bank Statement
        </button>
      </div>
    </div>
  );
}